import { useDrag } from "react-dnd";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteTrack,
  setCutActive,
  setCutFrame,
  updateSelectedTracks,
  updateTrackSelectedForSeek,
  setSelectSuperTrack,
  cutSuperTrack,
} from "@/store/supertrackSlice";
import { matchApi } from "../../api";
import { useParams } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  ListGroup,
  ListGroupItem,
  Overlay,
  Popover,
  Row,
  Spinner,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { useRef, useState } from "react";
import { toast } from "react-toastify";
import { CUT_MODES } from "@/constants";
import { useActions } from "@/hooks/useActions";
import useTask from "@/hooks/useTask";
import { callSaveFunction } from "@/store/communicationSlice";

export function SuperTrackMarkupTrack({ track, superTrackId, isDuplicate }) {
  const { videoId: matchId } = useParams();

  const dispatch = useDispatch();
  const {
    superTracks,
    selectedTracks,
    tracksMap,
    cutFrame,
    isCutActive,
    frameCount,
    superTrackStartTime,
    superTrackGlobalVideoDuration,
    isSelectSuperTrack,
  } = useSelector(({ superTracks }) => superTracks);

  const { cut, replace, substitute } = useActions();
  const { taskType } = useTask();
  const target = useRef();
  const [localIsCutActive, setLocalIsCutActive] = useState(false);
  const [isLoading] = useState(false);
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [cutMode, setCutMode] = useState(null);
  const token = localStorage.getItem("access");

  const [{ isDragging }, drag] = useDrag(() => ({
    type: "Track",
    item: { track, superTrackId },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId(),
    }),
  }));

  async function removeTrack() {
    setIsPopoverVisible(false);
    const result = window.confirm(`Удалить трек #${track.track_id}?`);

    if (result) {
      await matchApi.deleteTrack({ matchId, ...track, superTrackId });
      dispatch(deleteTrack({ ...track, superTrackId: Number(superTrackId) }));
    }
  }

  const opacity = isDragging ? 0.4 : 1;
  const selectedTracksFromStorage =
    JSON.parse(localStorage.getItem("selectedSupertracks")) || {};
  const isSelected = selectedTracksFromStorage[superTrackId];

  const isTrackCandidate = () => {
    if (!selectedTracks || !selectedTracks[0]) return;

    const sorted = selectedTracks
      .map((id) => tracksMap.get(id))
      .sort(({ frame_end: a }, { frame_end: b }) => b - a);

    if (!sorted.length || !sorted[0]) return false;

    return (
      sorted[0].frame_end <= track.frame_start &&
      sorted[0].frame_end + 60 >= track.frame_start
    );
  };

  const getBackGroundColor = () => {
    if (isTrackCandidate()) return "green";

    return isSelected ? "lightblue" : isDuplicate ? "#f77445" : "yellow";
  };

  const onSelectTrack = () => {
    dispatch(updateSelectedTracks({ isAdding: true, id: track.track_id }));
    setIsPopoverVisible(false);
  };

  const enableCut = (mode) => {
    setLocalIsCutActive(true);
    dispatch(setCutActive(true));
    onSelectTrack();
    dispatch(setCutFrame(track.frame_start));
    setIsPopoverVisible(false);
    setCutMode(mode);
  };

  const onCutOffTrack = async () => {
    try {
      setIsPopoverVisible(false);
      // Create a super track
      const newSuperTrack = await matchApi.createSuperTrack(matchId);
      if (!newSuperTrack) return;

      replace(matchId, superTrackId, newSuperTrack.super_track_uid, [
        track.track_id,
      ]);

      dispatch(
        cutSuperTrack({
          superTrackId,
          newSuperTrack,
          trackIds: [track.track_id],
        })
      );
    } catch (error) {
      console.error(error);
      toast(`Something went wrong. ${error.message}`);
    }
  };

  const disableCut = () => {
    dispatch(setCutActive(false));
    dispatch(setCutFrame(0));
    setLocalIsCutActive(false);
  };

  const submitCut = () => {
    if (!cutMode) {
      console.log("no cut mode");
      return;
    }
    cut(matchId, superTrackId, track.track_id, cutFrame, cutMode);
    disableCut();
  };

  const onCutFrameUpdate = (e) => {
    dispatch(setCutFrame(Number(e.target.value)));
  };

  const showPopover = () => {
    if (isCutActive) return;
    setIsPopoverVisible(true);
  };
  const selectAndSeekTrack = () => {
    setIsPopoverVisible(false);
    dispatch(updateTrackSelectedForSeek(track));
  };

  const isSelectSuperTrackState = () =>
    dispatch(setSelectSuperTrack(!isSelectSuperTrack));

  const onCutSuperTrack = async () => {
    try {
      if (taskType === "ball_supertrack") {
        toast.info("This action is not allowed for ball supertrack");
        return;
      }

      // Create a super track
      const newSuperTrack = await matchApi.createSuperTrack(matchId);
      if (!newSuperTrack) return;

      // TODO: let the backend decide what to move
      // filter tracks which need to be moved to a new super track
      const filteredTracks = superTracks[superTrackId].tracks.filter(
        (t, index) => {
          return t.frame_start >= track.frame_start;
        }
      );
      const filteredTrackIds = filteredTracks.map((t) => t.track_id);

      replace(
        matchId,
        superTrackId,
        newSuperTrack.super_track_uid,
        filteredTrackIds
      );

      dispatch(
        cutSuperTrack({
          superTrackId,
          newSuperTrack,
          trackIds: filteredTrackIds,
        })
      );
    } catch (error) {
      console.error(error);
      toast(`Something went wrong. ${error.message}`);
    } finally {
      setIsPopoverVisible(false);
    }
  };

  const onAddTask = async (isBall) => {
    try {
      await matchApi.addTask({
        match: matchId,
        task_type: "supertrack",
        task: {
          isBall: isBall,
          super_track_uid: parseInt(superTrackId),
        },
        token: token,
      });
      toast(`Task created`);
    } catch (error) {
      console.error(error);
      toast(`Something went wrong. ${error.message}`);
    }
  };

  const onSubstitute = async () => {
    substitute(matchId, superTrackId);
    dispatch(callSaveFunction(superTrackId));
  };

  return (
    <div
      className="super-track__track"
      ref={drag}
      style={{
        left:
          (track.frame_start -
            (frameCount / superTrackGlobalVideoDuration) *
              superTrackStartTime) *
            0.5 || 0,
        width: (track.frame_end - track.frame_start) * 0.5 || 0,
        opacity,
        backgroundColor: getBackGroundColor(),
      }}
      onClick={showPopover}
    >
      <div className="position-absolute start-0 ">#{track.track_id}</div>

      <div ref={target}>
        <Overlay
          show={localIsCutActive}
          placement="bottom-start"
          target={target.current}
          container={document.body}
        >
          <Popover style={{ width: "600px" }} id="popover-contained">
            <Popover.Header as="h3">Cut track #{track.track_id}</Popover.Header>
            <Popover.Body>
              <Row className="gap-2">
                <Form.Range
                  max={track.frame_end}
                  min={track.frame_start}
                  value={cutFrame}
                  onChange={onCutFrameUpdate}
                />
                <Col>
                  <Button
                    disabled={isLoading}
                    variant={"success"}
                    size={"sm"}
                    onClick={submitCut}
                  >
                    {isLoading && (
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                      />
                    )}
                    <span>Cut</span>
                  </Button>
                </Col>
                <Col>
                  <Button
                    disabled={isLoading}
                    variant="light"
                    size={"sm"}
                    onClick={disableCut}
                  >
                    Close
                  </Button>
                </Col>
              </Row>
            </Popover.Body>
          </Popover>
        </Overlay>
      </div>

      <Overlay
        rootClose
        show={isPopoverVisible}
        container={document.body}
        target={target.current}
        placement="bottom"
        popperConfig={{
          modifiers: [
            { name: "preventOverflow", options: { boundary: "viewport", padding: 8 } },
          ],
        }}
        onHide={() => setIsPopoverVisible(false)}
      >
        <Popover>
          <ListGroup>
            <ListGroupItem>Track Actions #{track.track_id}</ListGroupItem>

            <ListGroupItem action onClick={isSelectSuperTrackState}>
              {isSelectSuperTrack ? "Choose track" : "Choose supertrack"}
            </ListGroupItem>

            <ListGroupItem action onClick={onSelectTrack}>
              Choose
            </ListGroupItem>
            <ListGroupItem action onClick={() => selectAndSeekTrack()}>
              Select and play
            </ListGroupItem>
            <ListGroupItem action onClick={() => enableCut(CUT_MODES.CUT)}>
              Cut
            </ListGroupItem>
            <ListGroupItem
              action
              onClick={() => enableCut(CUT_MODES.CUT_OFF_RIGHT)}
            >
              Cut Off Right
            </ListGroupItem>
            <ListGroupItem
              action
              onClick={() => enableCut(CUT_MODES.CUT_OFF_LEFT)}
            >
              Cut Off Left
            </ListGroupItem>
            <ListGroupItem action onClick={onCutOffTrack}>
              Cut Off Track
            </ListGroupItem>
            <ListGroupItem action onClick={onCutSuperTrack}>
              Cut SuperTrack
            </ListGroupItem>
            <ListGroupItem action onClick={() => onAddTask(false)}>
              Add Task No Ball
            </ListGroupItem>
            <ListGroupItem action onClick={() => onAddTask(true)}>
              Add Task Ball
            </ListGroupItem>
            <ListGroupItem action onClick={removeTrack}>
              <FontAwesomeIcon icon={["fas", "trash"]} />
              <span>Delete</span>
            </ListGroupItem>
          </ListGroup>
        </Popover>
      </Overlay>
    </div>
  );
}
